import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { ApiResponse } from '@/lib/api-client';

export interface InvoiceNote {
  id: string;
  invoiceId: string;
  category: string | null;
  content: string;
  createdById: string;
  createdByName: string | null;
  createdAt: string;
  senderType: string;
  clientVisible: boolean;
}

const invoiceNotesKeys = {
  all: (invoiceId: string) => ['billing', 'invoices', invoiceId, 'notes'] as const,
};

function mapNote(raw: Record<string, unknown>): InvoiceNote {
  return {
    id: raw.id as string,
    invoiceId: (raw.invoice_id || raw.invoiceId) as string,
    category: (raw.category ?? null) as string | null,
    content: raw.content as string,
    createdById: (raw.created_by_id || raw.createdById || '') as string,
    createdByName: (raw.created_by_name || raw.createdByName || null) as string | null,
    createdAt: (raw.created_at || raw.createdAt) as string,
    senderType: (raw.sender_type || raw.senderType || 'staff') as string,
    clientVisible: (raw.client_visible ?? raw.clientVisible ?? false) as boolean,
  };
}

export function useInvoiceNotes(invoiceId: string) {
  return useQuery({
    queryKey: invoiceNotesKeys.all(invoiceId),
    queryFn: async () => {
      const response = await apiClient.get<ApiResponse<Record<string, unknown>[]>>(
        `/billing/invoices/${invoiceId}/notes`,
      );
      const raw = response.data.data ?? [];
      return raw.map(mapNote);
    },
    enabled: !!invoiceId,
  });
}

export function useCreateInvoiceNote(invoiceId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: {
      category: string | null;
      content: string;
      reminderDate?: string;
      reminderAssigneeId?: string;
    }) => {
      const response = await apiClient.post<ApiResponse<Record<string, unknown>>>(
        `/billing/invoices/${invoiceId}/notes`,
        {
          category: payload.category,
          content: payload.content,
          reminder_date: payload.reminderDate || null,
          reminder_assignee_id: payload.reminderAssigneeId || null,
        },
      );
      return mapNote(response.data.data as Record<string, unknown>);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceNotesKeys.all(invoiceId) });
    },
  });
}
